import { useState, useEffect, useRef, useCallback, useLayoutEffect, useReducer } from 'react';
import _slicedToArray from '@babel/runtime/helpers/slicedToArray';
import _defineProperty from '@babel/runtime/helpers/defineProperty';
import { breakpoints } from '@fremtind/jkl-core';

var PREFERS_REDUCED_MOTION$1 = "(prefers-reduced-motion: no-preference)";

var getInitialState = function getInitialState() {
  if (typeof window !== "undefined" && window.matchMedia) {
    return !window.matchMedia(PREFERS_REDUCED_MOTION$1).matches;
  }

  return false;
};

var useReducedMotion = function useReducedMotion() {
  var _useState = useState(getInitialState()),
      _useState2 = _slicedToArray(_useState, 2),
      prefersReducedMotion = _useState2[0],
      setPrefersReducedMotion = _useState2[1];

  useEffect(function () {
    var mediaQueryList;
    var listener;

    if (typeof window !== "undefined" && window.matchMedia) {
      var _mediaQueryList;

      mediaQueryList = window.matchMedia(PREFERS_REDUCED_MOTION$1);

      listener = function listener(event) {
        return setPrefersReducedMotion(!event.matches);
      };

      (_mediaQueryList = mediaQueryList) === null || _mediaQueryList === void 0 ? void 0 : _mediaQueryList.addListener(listener);
    }

    return function () {
      var _mediaQueryList2;

      (_mediaQueryList2 = mediaQueryList) === null || _mediaQueryList2 === void 0 ? void 0 : _mediaQueryList2.removeListener(listener);
    };
  }, []);
  return prefersReducedMotion;
};

function useAnimatedHeight(isOpen, options) {
  var raf1 = useRef();
  var raf2 = useRef();
  var elementRef = useRef(null);
  var firstRender = useRef(true);
  var prefersReducedMotion = useReducedMotion();

  function handleTransitionEnd(event) {
    var element = getElement(elementRef); // Ignore bubbling transitions from within container

    if (element && event.target === element) {
      var _options$onTransition;

      element.removeAttribute("style");
      options === null || options === void 0 ? void 0 : (_options$onTransition = options.onTransitionEnd) === null || _options$onTransition === void 0 ? void 0 : _options$onTransition.call(options, isOpen);
    }
  }

  var runAnimation = useCallback(function () {
    var _options$onTransition2;

    if (firstRender.current) {
      return; // Do not play animation on first render
    }

    options === null || options === void 0 ? void 0 : (_options$onTransition2 = options.onTransitionStart) === null || _options$onTransition2 === void 0 ? void 0 : _options$onTransition2.call(options, isOpen);

    if (prefersReducedMotion) {
      var _options$onTransition3;

      options === null || options === void 0 ? void 0 : (_options$onTransition3 = options.onTransitionEnd) === null || _options$onTransition3 === void 0 ? void 0 : _options$onTransition3.call(options, isOpen); // make sure to call callback when animation is off

      return;
    }

    var element = getElement(elementRef);

    if (element) {
      element.style.display = "block";
      element.style.overflow = "hidden";

      if (isOpen) {
        element.style.height = "0";
        element.style.height = "".concat(element.scrollHeight, "px");
      } else {
        // If the scrollHeight is 0 it means that we are transitioning from height 0 -> 0.
        // This causes the "transitionend"-event to never fire and the element gets stuck with
        // style: height: 0; display: block; overflow:hidden
        if (element.scrollHeight === 0) {
          element.removeAttribute("style");
          return;
        }

        element.style.height = "".concat(element.scrollHeight, "px");
        raf1.current = requestAnimationFrame(function () {
          raf2.current = requestAnimationFrame(function () {
            element.style.height = "".concat(0, "px");
          });
        });
      }
    }
  }, [isOpen]);
  useLayoutEffect(function () {
    runAnimation();
  }, [isOpen, runAnimation]);
  useEffect(function () {
    var element = getElement(elementRef);

    if (element) {
      element.addEventListener("transitionend", handleTransitionEnd);
    }

    return function () {
      if (element) {
        element.removeEventListener("transitionend", handleTransitionEnd);
      }
    };
  }, [isOpen]);
  useEffect(function () {
    var r1 = raf1.current;
    var r2 = raf2.current;
    firstRender.current = false;
    return function () {
      r1 && cancelAnimationFrame(r1);
      r2 && cancelAnimationFrame(r2);
    };
  }, [raf1, raf2]);
  return [elementRef, runAnimation];
}

function getElement(elementRef) {
  // Workaround to handle custom elements from NRK Core components until this lands:
  // https://github.com/nrkno/custom-element-to-react/pull/17
  return elementRef.current && (elementRef.current.el || elementRef.current);
}

function useClickOutside(ref, fn) {
  function handleClickOutside(event) {
    if (ref !== null && ref !== void 0 && ref.current && !ref.current.contains(event.target)) {
      fn();
    }
  }

  useEffect(function () {
    if (ref) {
      document && document.addEventListener("click", handleClickOutside);
    }

    return function () {
      if (ref) {
        document && document.removeEventListener("click", handleClickOutside);
      }
    };
  });
}

function useFocusOutside(ref, fn) {
  function handleFocusOutside(event) {
    if (ref !== null && ref !== void 0 && ref.current && !ref.current.contains(event.target)) {
      fn();
    }
  }

  useEffect(function () {
    if (ref) {
      document && document.addEventListener("focusin", handleFocusOutside);
    }

    return function () {
      if (ref) {
        document && document.removeEventListener("focusin", handleFocusOutside);
      }
    };
  });
}

function useKeyListener(ref, keys, fn) {
  if (typeof keys === "string") {
    keys = [keys];
  }

  function handleKeyDown(event) {
    if (keys && !keys.includes(event.key)) {
      return;
    }

    fn(event);
  }

  useEffect(function () {
    var element = ref && ref.current;

    if (element) {
      element.addEventListener("keydown", handleKeyDown);
    }

    return function () {
      if (element) {
        element.removeEventListener("keydown", handleKeyDown);
      }
    };
  });
}

var hasWindowWithMutationObserver = function hasWindowWithMutationObserver() {
  return typeof window !== "undefined" && typeof MutationObserver !== "undefined";
};

var useMutationObserver = function useMutationObserver(targetRef, onObservation, config) {
  var observerRef = useRef(null);
  useEffect(function () {
    var observer = observerRef.current;

    if (hasWindowWithMutationObserver()) {
      var target = targetRef.current;

      if (observer) {
        observer.disconnect();
      }

      observer = new MutationObserver(onObservation);

      if (target) {
        observer.observe(target, config);
      }
    }

    return function () {
      if (hasWindowWithMutationObserver() && observer) {
        observer.disconnect();
      }
    };
  }, [targetRef, onObservation, config]);
};

var hasWindowWithIntersectionObserver = function hasWindowWithIntersectionObserver() {
  return typeof window !== "undefined" && typeof IntersectionObserver !== "undefined";
};

var useIntersectionObserver = function useIntersectionObserver(targetRef, onIntersect, fallback, options) {
  var observerRef = useRef(null);
  useEffect(function () {
    var observer = observerRef.current;

    if (hasWindowWithIntersectionObserver()) {
      var target = targetRef.current;

      if (observer) {
        observer.disconnect();
      }

      observer = new IntersectionObserver(onIntersect, options);

      if (target) {
        observer.observe(target);
      }
    } else {
      fallback();
    }

    return function () {
      if (hasWindowWithIntersectionObserver() && observer) {
        observer.disconnect();
      }
    };
  }, [targetRef, onIntersect, fallback, options]);
};

function ownKeys$1(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); if (enumerableOnly) { symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; }); } keys.push.apply(keys, symbols); } return keys; }

function _objectSpread$1(target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i] != null ? arguments[i] : {}; if (i % 2) { ownKeys$1(Object(source), true).forEach(function (key) { _defineProperty(target, key, source[key]); }); } else if (Object.getOwnPropertyDescriptors) { Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)); } else { ownKeys$1(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } } return target; }
var ActionTypes;

(function (ActionTypes) {
  ActionTypes["resized"] = "WINDOW_RESIZED";
})(ActionTypes || (ActionTypes = {}));

var actionTypes = {
  resized: ActionTypes.resized
};

var setDeviceSize = function setDeviceSize(width, height) {
  return {
    isSmallDevice: width <= breakpoints.small,
    isMediumDevice: width > breakpoints.small && width < breakpoints.medium,
    isLargeDevice: width > breakpoints.medium && width < breakpoints.large,
    isXlDevice: width > breakpoints.large,
    isPortrait: height >= width,
    isLandscape: height < width,
    inner: {
      height: height,
      width: width
    }
  };
};

var initialState = {
  isSmallDevice: false,
  isMediumDevice: false,
  isLargeDevice: false,
  isXlDevice: false,
  isLandscape: false,
  isPortrait: false,
  inner: {
    height: 0,
    width: 0
  }
};
var init = function init() {
  var width = typeof window !== "undefined" ? window.innerWidth : 0;
  var height = typeof window !== "undefined" ? window.innerHeight : 0;
  return setDeviceSize(width, height);
};
var reducer = function reducer(state, _ref) {
  var type = _ref.type,
      width = _ref.width,
      height = _ref.height;

  switch (type) {
    case actionTypes.resized:
      return _objectSpread$1(_objectSpread$1({}, state), setDeviceSize(width, height));
  }
};

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); if (enumerableOnly) { symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; }); } keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i] != null ? arguments[i] : {}; if (i % 2) { ownKeys(Object(source), true).forEach(function (key) { _defineProperty(target, key, source[key]); }); } else if (Object.getOwnPropertyDescriptors) { Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)); } else { ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } } return target; }
var useScreen = function useScreen() {
  var _useReducer = useReducer(reducer, initialState, init),
      _useReducer2 = _slicedToArray(_useReducer, 2),
      device = _useReducer2[0],
      deviceDispatch = _useReducer2[1];

  var handleScreenChange = function handleScreenChange() {
    return requestAnimationFrame(function () {
      return deviceDispatch({
        type: actionTypes.resized,
        width: window.innerWidth,
        height: window.innerHeight
      });
    });
  };

  useEffect(function () {
    if (typeof window !== "undefined") {
      window.addEventListener("resize", handleScreenChange);
    }

    return function () {
      if (typeof window !== "undefined") {
        window.removeEventListener("resize", handleScreenChange);
      }
    };
  }, []);
  return _objectSpread({}, device);
};

var useScrollIntoView = function useScrollIntoView(_ref) {
  var ref = _ref.ref,
      _ref$timeout = _ref.timeout,
      timeout = _ref$timeout === void 0 ? 0 : _ref$timeout,
      _ref$autoScroll = _ref.autoScroll,
      autoScroll = _ref$autoScroll === void 0 ? true : _ref$autoScroll,
      _ref$options = _ref.options,
      options = _ref$options === void 0 ? {
    behavior: "smooth"
  } : _ref$options;

  var scrollIntoView = function scrollIntoView() {
    if (ref !== null && ref !== void 0 && ref.current) {
      ref.current.scrollIntoView(options);
    }
  };

  useEffect(function () {
    if (!autoScroll) {
      return;
    }

    var scrollFn = setTimeout(scrollIntoView, timeout);
    return function () {
      return clearTimeout(scrollFn);
    };
  }, [ref, timeout, autoScroll]);
  return [scrollIntoView];
};

var PREFERS_REDUCED_MOTION = "(prefers-reduced-motion: reduce)";
var PREFERS_LIGHT_COLOR_SCHEME = "(prefers-color-scheme: light)";

var getInitialMediaQueryMatch = function getInitialMediaQueryMatch(mediaQuery) {
  if (typeof window !== "undefined" && window.matchMedia) {
    return window.matchMedia(mediaQuery).matches;
  }

  return false;
};

var addMediaQueryListener = function addMediaQueryListener(mq, callback) {
  if (typeof mq.addEventListener !== "undefined") {
    mq.addEventListener("change", callback);
  } else {
    mq.addListener(callback);
  }
};

var useBrowserPreferences = function useBrowserPreferences() {
  var _useState = useState(getInitialMediaQueryMatch(PREFERS_REDUCED_MOTION)),
      _useState2 = _slicedToArray(_useState, 2),
      prefersReducedMotion = _useState2[0],
      setPrefersReducedMotion = _useState2[1];

  var _useState3 = useState(getInitialMediaQueryMatch(PREFERS_LIGHT_COLOR_SCHEME) ? "light" : "dark"),
      _useState4 = _slicedToArray(_useState3, 2),
      prefersColorScheme = _useState4[0],
      setPrefersColorScheme = _useState4[1];

  useEffect(function () {
    if (typeof window !== "undefined" && window.matchMedia) {
      addMediaQueryListener(window.matchMedia(PREFERS_REDUCED_MOTION), function (e) {
        setPrefersReducedMotion(e.matches);
      });
      addMediaQueryListener(window.matchMedia(PREFERS_LIGHT_COLOR_SCHEME), function (e) {
        setPrefersColorScheme(e.matches ? "light" : "dark");
      });
    }
  }, []);
  return {
    prefersReducedMotion: prefersReducedMotion,
    prefersColorScheme: prefersColorScheme
  };
};

var useProgressiveImg = function useProgressiveImg(_ref) {
  var lowQualitySrc = _ref.lowQualitySrc,
      highQualitySrc = _ref.highQualitySrc;

  var _useState = useState(lowQualitySrc),
      _useState2 = _slicedToArray(_useState, 2),
      src = _useState2[0],
      setSrc = _useState2[1];

  useEffect(function () {
    setSrc(lowQualitySrc);
    var img = new Image();
    img.src = highQualitySrc;

    img.onload = function () {
      setSrc(highQualitySrc);
    };
  }, [lowQualitySrc, highQualitySrc]);
  return {
    src: src,
    isBlurred: src === lowQualitySrc
  };
};

var usePreviousValue = function usePreviousValue(value) {
  var ref = useRef();
  useEffect(function () {
    ref.current = value;
  });
  return ref.current;
};

export { initialState as initialScreenState, useAnimatedHeight, useBrowserPreferences, useClickOutside, useFocusOutside, useIntersectionObserver, useKeyListener, useMutationObserver, usePreviousValue, useProgressiveImg, useReducedMotion, useScreen, useScrollIntoView };
